// src/providers/ReactQueryProvider.tsx

import { ReactNode } from "react";

import { focusManager, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

interface ReactQueryProviderProps {
  children: ReactNode;
}

/**
 * Por padrão o React Query só observa a visibilidade da aba. Aqui ele passa a considerar também
 * o foco da janela (trocar de app): o polling (`refetchInterval`) pausa sem foco e o
 * `refetchOnWindowFocus` dispara uma única vez quando o foco volta — o focusManager só avisa
 * quando o estado muda, então "aba visível" + "janela focada" não geram duas consultas.
 */
focusManager.setEventListener((definirFoco) => {
  const atualizar = () =>
    definirFoco(document.visibilityState === "visible" && document.hasFocus());

  window.addEventListener("focus", atualizar);
  window.addEventListener("blur", atualizar);
  document.addEventListener("visibilitychange", atualizar);
  return () => {
    window.removeEventListener("focus", atualizar);
    window.removeEventListener("blur", atualizar);
    document.removeEventListener("visibilitychange", atualizar);
  };
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: 1000 * 60 * 1, // 1 minuto evita que dados sejam refetchados por 1 minuto (ótimo para listas com paginação).
      retry: 2, // evita retries infinitos em caso de erro (comportamento seguro para usuários).
      refetchOnWindowFocus: true, // evita refetch automático ao voltar para a aba (ideal para apps com controle fino de atualização como o seu).
    },
  },
});

export function ReactQueryProvider({ children }: ReactQueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
